// Supabase Client Configuration
// Replace these values with your actual Supabase project credentials
// Get them from: Supabase Dashboard → Settings → API

import { createClient } from '@supabase/supabase-js';

// Your Supabase project URL
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';

// Your Supabase anon/public key (safe for client-side)
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Validate that credentials are set
if (!supabaseUrl || supabaseUrl === '' || supabaseUrl.includes('YOUR_PROJECT_URL')) {
  console.error('❌ Supabase URL is not configured!');
  console.error('Please create a .env file in the project root with:');
  console.error('REACT_APP_SUPABASE_URL=your_supabase_url');
  console.error('Get your URL from: Supabase Dashboard → Settings → API');
}

if (!supabaseAnonKey || supabaseAnonKey === '' || supabaseAnonKey.includes('YOUR_ANON_KEY')) {
  console.error('❌ Supabase Anon Key is not configured!');
  console.error('Please create a .env file in the project root with:');
  console.error('REACT_APP_SUPABASE_ANON_KEY=your_anon_key');
  console.error('Get your key from: Supabase Dashboard → Settings → API');
}

// Create Supabase client with real-time enabled (will throw error if URL is invalid)
let supabase;
try {
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  });
} catch (error) {
  console.error('❌ Failed to create Supabase client:', error.message);
  console.error('Please check your .env file and ensure both REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY are set correctly.');
  // Create a dummy client to prevent app crash, but it won't work
  supabase = createClient('https://placeholder.supabase.co', 'placeholder-key');
}

// Export the supabase client
export { supabase };

// ============================================
// HELPER FUNCTIONS FOR COMMON OPERATIONS
// ============================================

// Users
export const getUsers = async () => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .order('registration_date', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const getUserById = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();
  
  if (error) throw error;
  return data;
};

// Flights
export const getFlights = async () => {
  const { data, error } = await supabase
    .from('flights')
    .select(`
      *,
      routes (
        from_airport_id,
        to_airport_id,
        destinations_from:destinations!routes_from_airport_id_fkey (
          city_name,
          airport_code
        ),
        destinations_to:destinations!routes_to_airport_id_fkey (
          city_name,
          airport_code
        )
      )
    `)
    .eq('status', 'active')
    .order('departure_date', { ascending: true });
  
  if (error) throw error;
  return data;
};

export const getFlightById = async (flightId) => {
  const { data, error } = await supabase
    .from('flights')
    .select(`
      *,
      routes (
        *,
        destinations_from:destinations!routes_from_airport_id_fkey (*),
        destinations_to:destinations!routes_to_airport_id_fkey (*)
      )
    `)
    .eq('id', flightId)
    .single();
  
  if (error) throw error;
  return data;
};

export const createFlight = async (flightData) => {
  const { data, error } = await supabase
    .from('flights')
    .insert([flightData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Bookings
export const getBookings = async () => {
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      users (*),
      flights (
        *,
        routes (
          destinations_from:destinations!routes_from_airport_id_fkey (*),
          destinations_to:destinations!routes_to_airport_id_fkey (*)
        )
      )
    `)
    .order('booking_date', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const getBookingById = async (bookingId) => {
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      users (*),
      flights (
        *,
        routes (
          destinations_from:destinations!routes_from_airport_id_fkey (*),
          destinations_to:destinations!routes_to_airport_id_fkey (*)
        )
      ),
      payments (*)
    `)
    .eq('booking_id', bookingId)
    .single();
  
  if (error) throw error;
  return data;
};

export const createBooking = async (bookingData) => {
  // First, generate booking ID
  const { data: bookingIdData, error: idError } = await supabase
    .rpc('generate_booking_id');
  
  if (idError) throw idError;
  
  // Create booking with generated ID
  const { data, error } = await supabase
    .from('bookings')
    .insert([{
      ...bookingData,
      booking_id: bookingIdData
    }])
    .select()
    .single();
  
  if (error) throw error;
  
  // Create timeline entry
  await supabase
    .from('booking_timeline')
    .insert([{
      booking_id: data.id,
      event_type: 'created',
      event_description: 'Booking created'
    }]);
  
  return data;
};

// Payments
export const getPayments = async () => {
  const { data, error } = await supabase
    .from('payments')
    .select(`
      *,
      bookings (*),
      users (*)
    `)
    .order('payment_date', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const createPayment = async (paymentData) => {
  const { data, error } = await supabase
    .from('payments')
    .insert([paymentData])
    .select()
    .single();
  
  if (error) throw error;
  
  // Update booking payment status
  await supabase
    .from('bookings')
    .update({ payment_status: 'paid' })
    .eq('id', paymentData.booking_id);
  
  // Add timeline entry
  await supabase
    .from('booking_timeline')
    .insert([{
      booking_id: paymentData.booking_id,
      event_type: 'paid',
      event_description: `Payment completed via ${paymentData.payment_method}`
    }]);
  
  return data;
};

// Destinations
export const getDestinations = async () => {
  const { data, error } = await supabase
    .from('destinations')
    .select('*')
    .eq('status', 'active')
    .order('city_name', { ascending: true });
  
  if (error) throw error;
  return data;
};

export const createDestination = async (destinationData) => {
  const { data, error } = await supabase
    .from('destinations')
    .insert([destinationData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Routes
export const getRoutes = async () => {
  const { data, error } = await supabase
    .from('routes')
    .select(`
      *,
      destinations_from:destinations!routes_from_airport_id_fkey (*),
      destinations_to:destinations!routes_to_airport_id_fkey (*)
    `)
    .eq('status', 'active')
    .order('airline', { ascending: true });
  
  if (error) throw error;
  return data;
};

export const createRoute = async (routeData) => {
  const { data, error } = await supabase
    .from('routes')
    .insert([routeData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Promotions
export const getPromotions = async () => {
  const { data, error } = await supabase
    .from('promotions')
    .select('*')
    .order('start_date', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const getActivePromotions = async () => {
  const { data, error } = await supabase
    .from('promotions')
    .select('*')
    .eq('status', 'active')
    .gte('end_date', new Date().toISOString().split('T')[0])
    .lte('start_date', new Date().toISOString().split('T')[0])
    .order('start_date', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const validatePromotionCode = async (code) => {
  const { data, error } = await supabase
    .from('promotions')
    .select('*')
    .eq('code', code.toUpperCase())
    .eq('status', 'active')
    .gte('end_date', new Date().toISOString().split('T')[0])
    .lte('start_date', new Date().toISOString().split('T')[0])
    .single();
  
  if (error) return null;
  return data;
};

export const createPromotion = async (promotionData) => {
  const { data, error } = await supabase
    .from('promotions')
    .insert([{
      ...promotionData,
      code: promotionData.code.toUpperCase()
    }])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Support Tickets
export const getSupportTickets = async () => {
  const { data, error } = await supabase
    .from('support_tickets')
    .select(`
      *,
      users (*),
      bookings (*)
    `)
    .order('submitted_date', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const getTicketById = async (ticketId) => {
  const { data, error } = await supabase
    .from('support_tickets')
    .select(`
      *,
      users (*),
      bookings (*),
      ticket_messages (*)
    `)
    .eq('ticket_id', ticketId)
    .single();
  
  if (error) throw error;
  return data;
};

export const createSupportTicket = async (ticketData) => {
  // Generate ticket ID
  const { data: ticketIdData, error: idError } = await supabase
    .rpc('generate_ticket_id');
  
  if (idError) throw idError;
  
  const { data, error } = await supabase
    .from('support_tickets')
    .insert([{
      ...ticketData,
      ticket_id: ticketIdData
    }])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const addTicketMessage = async (messageData) => {
  const { data, error } = await supabase
    .from('ticket_messages')
    .insert([messageData])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Dashboard Statistics
export const getDashboardStats = async () => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  // Total bookings
  const { count: totalBookings } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .gte('booking_date', thirtyDaysAgo.toISOString());
  
  // Total revenue
  const { data: payments } = await supabase
    .from('payments')
    .select('amount')
    .eq('status', 'success')
    .gte('payment_date', thirtyDaysAgo.toISOString());
  
  const totalRevenue = payments?.reduce((sum, p) => sum + parseFloat(p.amount), 0) || 0;
  
  // Active users
  const { count: activeUsers } = await supabase
    .from('users')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'active')
    .gte('registration_date', thirtyDaysAgo.toISOString());
  
  // Pending refunds
  const { count: pendingRefunds } = await supabase
    .from('payments')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'refunded')
    .gte('payment_date', thirtyDaysAgo.toISOString());
  
  return {
    totalBookings: totalBookings || 0,
    totalRevenue,
    activeUsers: activeUsers || 0,
    pendingRefunds: pendingRefunds || 0
  };
};

// Admin Users
export const adminLogin = async (username, password) => {
  // Note: In production, use Supabase Auth or proper password hashing
  const { data, error } = await supabase
    .from('admin_users')
    .select('*')
    .eq('username', username)
    .eq('status', 'active')
    .single();
  
  if (error) throw error;
  
  // In production, verify password hash here
  // For now, this is a placeholder
  return data;
};

// ============================================
// FLIGHT BOOKINGS TABLE FUNCTIONS
// ============================================
/**
 * Get all flight bookings
 */
export const getFlightBookings = async (filters = {}) => {
  try {
    // First, try to get bookings without user join to avoid join errors
    let query = supabase
      .from('flight_bookings')
      .select(`
        *,
        booking_passengers (*),
        booking_payments (*),
        booking_addons (*),
        booking_timeline (*)
      `);

    // Apply filters
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.bookingStatus) {
      query = query.eq('booking_status', filters.bookingStatus);
    }
    if (filters.paymentStatus) {
      query = query.eq('payment_status', filters.paymentStatus);
    }
    if (filters.bookingReference) {
      query = query.eq('booking_reference', filters.bookingReference);
    }

    query = query.order('booking_date', { ascending: false });

    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching flight bookings:', error);
      throw error;
    }

    // If we have data, try to fetch user info separately for each booking
    if (data && data.length > 0) {
      const bookingsWithUsers = await Promise.all(
        data.map(async (booking) => {
          if (booking.user_id) {
            try {
              const { data: userData, error: userError } = await supabase
                .from('users')
                .select('id, name, email, phone')
                .eq('id', booking.user_id)
                .single();
              
              if (!userError && userData) {
                booking.users = [userData];
              }
            } catch (err) {
              console.warn('Could not fetch user for booking:', booking.id, err);
            }
          }
          return booking;
        })
      );
      return bookingsWithUsers;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getFlightBookings:', error);
    throw error;
  }
};

/**
 * Get flight booking by reference
 */
export const getFlightBookingByReference = async (bookingReference) => {
  const { data, error } = await supabase
    .from('flight_bookings')
    .select(`
      *,
      booking_passengers (*),
      booking_payments (*),
      booking_addons (*),
      booking_timeline (*)
    `)
    .eq('booking_reference', bookingReference)
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Get flight booking by ID
 */
export const getFlightBookingById = async (bookingId) => {
  const { data, error } = await supabase
    .from('flight_bookings')
    .select(`
      *,
      booking_passengers (*),
      booking_payments (*),
      booking_addons (*),
      booking_timeline (*)
    `)
    .eq('id', bookingId)
    .single();
  
  if (error) throw error;
  return data;
};

/**
 * Get flight bookings for a user
 */
export const getUserFlightBookings = async (userId) => {
  const { data, error } = await supabase
    .from('flight_bookings')
    .select(`
      *,
      booking_passengers (*),
      booking_payments (*),
      booking_addons (*)
    `)
    .eq('user_id', userId)
    .order('booking_date', { ascending: false });
  
  if (error) throw error;
  return data;
};

/**
 * Update flight booking status
 */
export const updateFlightBookingStatus = async (bookingId, status) => {
  const { data, error } = await supabase
    .from('flight_bookings')
    .update({ 
      booking_status: status,
      updated_at: new Date().toISOString()
    })
    .eq('id', bookingId)
    .select()
    .single();
  
  if (error) throw error;
  
  // Add timeline entry
  await supabase.rpc('add_booking_timeline_entry', {
    p_booking_id: bookingId,
    p_event_type: 'status_updated',
    p_event_description: `Booking status updated to ${status}`,
    p_performed_by_type: 'system'
  });
  
  return data;
};

/**
 * Update payment status
 */
export const updatePaymentStatus = async (bookingId, paymentStatus) => {
  const { data, error } = await supabase
    .from('flight_bookings')
    .update({ 
      payment_status: paymentStatus,
      payment_date: paymentStatus === 'paid' ? new Date().toISOString() : null,
      updated_at: new Date().toISOString()
    })
    .eq('id', bookingId)
    .select()
    .single();
  
  if (error) throw error;
  
  // Add timeline entry
  await supabase.rpc('add_booking_timeline_entry', {
    p_booking_id: bookingId,
    p_event_type: 'payment_updated',
    p_event_description: `Payment status updated to ${paymentStatus}`,
    p_performed_by_type: 'system'
  });
  
  return data;
};

// ============================================
// FLIGHT COMPARISONS FUNCTIONS
// ============================================

/**
 * Get or create a session ID for anonymous users
 */
export const getSessionId = () => {
  let sessionId = localStorage.getItem('session_id');
  if (!sessionId) {
    // Generate a unique session ID
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

/**
 * Get user ID if logged in
 */
export const getUserId = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch {
    return null;
  }
};

/**
 * Save a flight to comparison in Supabase
 */
export const saveFlightComparison = async (flight, searchData = {}) => {
  try {
    const userId = await getUserId();
    const sessionId = userId ? null : getSessionId();
    
    // Extract price values
    const priceTotal = flight.price?.total 
      ? (typeof flight.price.total === 'number' ? flight.price.total : parseFloat(flight.price.total))
      : (typeof flight.total === 'number' ? flight.total : parseFloat(flight.total || 0));
    
    const priceBase = flight.price?.base 
      ? (typeof flight.price.base === 'number' ? flight.price.base : parseFloat(flight.price.base))
      : (typeof flight.baseFare === 'number' ? flight.baseFare : (flight.baseFare ? parseFloat(String(flight.baseFare).replace(/[^\d.]/g, '')) : null));
    
    const priceTaxes = flight.price?.taxes 
      ? (typeof flight.price.taxes === 'number' ? flight.price.taxes : parseFloat(flight.price.taxes))
      : (typeof flight.taxes === 'number' ? flight.taxes : (flight.taxes ? parseFloat(String(flight.taxes).replace(/[^\d.]/g, '')) : null));

    const comparisonData = {
      user_id: userId || null,
      session_id: sessionId,
      flight_data: flight, // Store complete flight object as JSONB
      flight_id: flight.id || flight.offer_id || null,
      offer_id: flight.offer_id || flight.id || null,
      airline: flight.airline || null,
      departure_airport: flight.departureAirport || null,
      arrival_airport: flight.arrivalAirport || null,
      departure_time: flight.departureTime || null,
      arrival_time: flight.arrivalTime || null,
      duration: flight.duration || null,
      stops: flight.stops || 0,
      price_total: priceTotal || null,
      price_base: priceBase || null,
      price_taxes: priceTaxes || null,
      price_currency: flight.price?.currency || 'NGN',
      aircraft_type: flight.aircraftType || null,
      travel_class: flight.travelClass || flight.cabinClass || null,
      amenities: Array.isArray(flight.amenities) ? flight.amenities : null,
      search_from: searchData.from || null,
      search_to: searchData.to || null,
      search_departure_date: searchData.departureDate || null,
      search_return_date: searchData.returnDate || null,
      search_passengers: searchData.passengers ? (typeof searchData.passengers === 'object' 
        ? (searchData.passengers.adults || 0) + (searchData.passengers.children || 0) + (searchData.passengers.infants || 0)
        : parseInt(searchData.passengers)) : null,
      search_trip_type: searchData.tripType || null
    };

    const { data, error } = await supabase
      .from('flight_comparisons')
      .insert([comparisonData])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving flight comparison:', error);
    throw error;
  }
};

/**
 * Remove a flight from comparison in Supabase
 */
export const removeFlightComparison = async (flightId) => {
  try {
    const userId = await getUserId();
    const sessionId = userId ? null : getSessionId();
    
    let query = supabase
      .from('flight_comparisons')
      .delete();
    
    if (userId) {
      query = query.eq('user_id', userId);
    } else {
      query = query.eq('session_id', sessionId);
    }
    
    query = query.or(`flight_id.eq.${flightId},offer_id.eq.${flightId}`);
    
    const { error } = await query;
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing flight comparison:', error);
    throw error;
  }
};

/**
 * Get all flights in comparison for current user/session
 */
export const getFlightComparisons = async () => {
  try {
    const userId = await getUserId();
    const sessionId = userId ? null : getSessionId();
    
    let query = supabase
      .from('flight_comparisons')
      .select('*')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    
    if (userId) {
      query = query.eq('user_id', userId);
    } else {
      query = query.eq('session_id', sessionId);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    
    // Convert back to flight objects from flight_data JSONB
    return data.map(item => item.flight_data);
  } catch (error) {
    console.error('Error getting flight comparisons:', error);
    // Fallback to localStorage if Supabase fails
    try {
      const saved = localStorage.getItem('compareFlights');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }
};

/**
 * Clear all comparisons for current user/session
 */
export const clearFlightComparisons = async () => {
  try {
    const userId = await getUserId();
    const sessionId = userId ? null : getSessionId();
    
    let query = supabase
      .from('flight_comparisons')
      .delete();
    
    if (userId) {
      query = query.eq('user_id', userId);
    } else {
      query = query.eq('session_id', sessionId);
    }
    
    const { error } = await query;
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error clearing flight comparisons:', error);
    // Also clear localStorage
    localStorage.removeItem('compareFlights');
    throw error;
  }
};